/**
 * 动作注册表（ui-spec §7.4）：页面只能通过 ActionRef 引用动作，级别与审计集中登记。
 */
import type { IconName } from '../ui/icons';
import { zh } from '../i18n/zh';

export type ActionLevel = 'L1' | 'L2' | 'L3';
export type ActionKind = 'create' | 'read' | 'update' | 'delete' | 'run';
export type ActionScope = 'global' | 'hub';

export interface ActionMeta {
  object: string;
  kind: ActionKind;
  scope: ActionScope;
  /** i18n 字典中的按钮文案键 */
  label: keyof typeof zh.actions;
  /** 危险级别；读取类动作为空 */
  level?: ActionLevel;
  parent?: string;
  /** 唯一创建入口所在路由（S1） */
  entry?: string;
  form?: 'drawer' | 'wizard' | 'inline' | 'confirm' | 'none';
  audit?: string;
  /** 是否为节点上的长任务 */
  task?: boolean;
  icon?: IconName;
  /** L3 必须提供确认输入的对象名（运行时值由页面给出） */
  confirmName?: string;
}

export const actions = {
  'hub.create': {
    object: 'hub',
    kind: 'create',
    scope: 'global',
    label: 'hub.create',
    level: 'L1',
    entry: '/hubs',
    form: 'drawer',
    audit: 'hub.created',
    icon: 'plus',
  },
  'hub.open': {
    object: 'hub',
    kind: 'read',
    scope: 'global',
    label: 'hub.open',
    form: 'none',
    audit: 'hub.opened',
    icon: 'server',
  },
  'hub.delete': {
    object: 'hub',
    kind: 'delete',
    scope: 'global',
    label: 'hub.delete',
    level: 'L3',
    form: 'confirm',
    audit: 'hub.deleted',
    confirmName: 'hub',
  },
  'hub.statusRefresh': {
    object: 'hub',
    kind: 'read',
    scope: 'global',
    label: 'hub.statusRefresh',
    form: 'none',
    icon: 'refresh',
  },
  'app.save': {
    object: 'app',
    kind: 'update',
    scope: 'hub',
    label: 'app.save',
    level: 'L1',
    form: 'inline',
    audit: 'app.config_saved',
  },
  'app.plan': {
    object: 'app',
    kind: 'read',
    scope: 'hub',
    label: 'app.plan',
    level: 'L1',
    form: 'inline',
    icon: 'terminal',
  },
  'app.apply': {
    object: 'app',
    kind: 'update',
    scope: 'hub',
    label: 'app.apply',
    level: 'L2',
    form: 'inline',
    audit: 'app.applied',
    task: true,
  },
  'app.install': {
    object: 'app',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'app.install',
    level: 'L2',
    entry: '/hubs/:hubId/sites',
    form: 'confirm',
    audit: 'app.installed',
    task: true,
    icon: 'download',
  },
  'app.uninstall': {
    object: 'app',
    kind: 'delete',
    scope: 'hub',
    label: 'app.uninstall',
    level: 'L3',
    form: 'confirm',
    audit: 'app.uninstalled',
    task: true,
    confirmName: 'app',
  },
  'app.rollback': {
    object: 'app',
    kind: 'update',
    scope: 'hub',
    label: 'app.rollback',
    level: 'L2',
    form: 'confirm',
    audit: 'app.rolled_back',
    icon: 'refresh',
  },
  'peer.create': {
    object: 'peer',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.create',
    level: 'L1',
    entry: '/hubs/:hubId/wireguard',
    form: 'drawer',
    audit: 'peer.created',
    icon: 'plus',
  },
  'peer.update': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.update',
    level: 'L1',
    form: 'drawer',
    audit: 'peer.updated',
    icon: 'settings',
  },
  'peer.publishSite': {
    object: 'peer',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.publishSite',
    form: 'none',
    audit: 'peer.published_as_site',
    icon: 'globe',
  },
  'peer.delete': {
    object: 'peer',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'peer.deleted',
  },
  'peer.disable': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.disable',
    level: 'L1',
    form: 'none',
    audit: 'peer.disabled',
    icon: 'pause',
  },
  'peer.enable': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.enable',
    level: 'L1',
    form: 'none',
    audit: 'peer.enabled',
    icon: 'check',
  },
  'peer.exportConfig': {
    object: 'peer',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.exportConfig',
    form: 'none',
    audit: 'peer.config_viewed',
    icon: 'download',
  },
  'peer.rotate': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.rotate',
    level: 'L2',
    form: 'confirm',
    audit: 'peer.rotate.started',
    icon: 'refresh',
  },
  'peer.confirmRotation': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.confirmRotation',
    level: 'L1',
    form: 'none',
    audit: 'peer.rotate.confirmed',
    icon: 'check',
  },
  'peer.cancelRotation': {
    object: 'peer',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'peer.cancelRotation',
    level: 'L1',
    form: 'none',
    audit: 'peer.rotate.canceled',
    icon: 'x',
  },
  'wg.interface.update': {
    object: 'wg',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'wg.interface.update',
    level: 'L1',
    form: 'drawer',
    audit: 'wg.interface.updated',
    icon: 'settings',
  },
  'wg.apply': {
    object: 'wg',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'wg.apply',
    level: 'L2',
    form: 'none',
    audit: 'wg.applied',
    icon: 'refresh',
  },
  'cert.create': {
    object: 'cert',
    kind: 'create',
    scope: 'global',
    label: 'cert.create',
    level: 'L1',
    entry: '/settings?tab=certs',
    form: 'drawer',
    audit: 'cert.created',
    icon: 'plus',
  },
  'cert.export': {
    object: 'cert',
    kind: 'read',
    scope: 'global',
    label: 'cert.export',
    level: 'L2',
    form: 'confirm',
    audit: 'cert.material',
    icon: 'download',
  },
  'cert.delete': {
    object: 'cert',
    kind: 'delete',
    scope: 'global',
    label: 'cert.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'cert.deleted',
  },
  'cfDns.account.create': {
    object: 'cfDnsAccount',
    kind: 'create',
    scope: 'global',
    label: 'cfDns.account.create',
    level: 'L1',
    entry: '/settings',
    form: 'drawer',
    audit: 'cfDns.account.create',
    icon: 'plus',
  },
  'cfDns.account.update': {
    object: 'cfDnsAccount',
    kind: 'update',
    scope: 'global',
    label: 'cfDns.account.update',
    level: 'L1',
    form: 'drawer',
    audit: 'cfDns.account.update',
    icon: 'settings',
  },
  'cfDns.account.delete': {
    object: 'cfDnsAccount',
    kind: 'delete',
    scope: 'global',
    label: 'cfDns.account.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'cfDns.account.delete',
  },
  'setting.create': {
    object: 'setting',
    kind: 'create',
    scope: 'global',
    label: 'setting.create',
    level: 'L1',
    entry: '/settings',
    form: 'drawer',
    audit: 'setting.put',
    icon: 'plus',
  },
  'setting.update': {
    object: 'setting',
    kind: 'update',
    scope: 'global',
    label: 'setting.update',
    level: 'L1',
    form: 'drawer',
    audit: 'setting.put',
    icon: 'settings',
  },
  'setting.delete': {
    object: 'setting',
    kind: 'delete',
    scope: 'global',
    label: 'setting.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'setting.delete',
  },
  'site.create': {
    object: 'site',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'site.create',
    level: 'L1',
    entry: '/hubs/:hubId/sites',
    form: 'drawer',
    audit: 'site.created',
    icon: 'plus',
  },
  'site.update': {
    object: 'site',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'site.update',
    level: 'L1',
    form: 'drawer',
    audit: 'site.updated',
    icon: 'settings',
  },
  'site.delete': {
    object: 'site',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'site.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'site.deleted',
  },
  'site.enable': {
    object: 'site',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'site.enable',
    level: 'L1',
    form: 'none',
    audit: 'site.enabled',
    icon: 'check',
  },
  'site.disable': {
    object: 'site',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'site.disable',
    level: 'L1',
    form: 'none',
    audit: 'site.disabled',
    icon: 'pause',
  },
  'site.clear': {
    object: 'site',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'site.clear',
    level: 'L2',
    form: 'confirm',
    audit: 'site.cleared',
    icon: 'x',
  },
  'caddy.service': {
    object: 'caddy',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'caddy.service',
    form: 'none',
    audit: 'caddy.service_viewed',
    icon: 'terminal',
  },
  'caddy.reload': {
    object: 'caddy',
    kind: 'run',
    scope: 'hub',
    parent: 'hub',
    label: 'caddy.reload',
    level: 'L2',
    form: 'confirm',
    audit: 'caddy.reloaded',
    icon: 'refresh',
  },
  'caddy.export': {
    object: 'caddy',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'caddy.export',
    level: 'L2',
    form: 'confirm',
    audit: 'caddy.config_exported',
    icon: 'download',
  },
  'agent.installCommand': {
    object: 'agent',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'agent.installCommand',
    level: 'L1',
    form: 'drawer',
    icon: 'download',
  },
  'agent.upgradeCommand': {
    object: 'agent',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'agent.upgradeCommand',
    level: 'L1',
    form: 'drawer',
    icon: 'refresh',
  },
  'agent.remoteUpgrade': {
    object: 'agent',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'agent.remoteUpgrade',
    level: 'L3',
    form: 'confirm',
    audit: 'agent.remote_upgraded',
    icon: 'refresh',
  },
  'agent.uninstallCommand': {
    object: 'agent',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'agent.uninstallCommand',
    level: 'L1',
    form: 'drawer',
    icon: 'x',
  },
  'node.mode.open': {
    object: 'nodeMode',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'node.mode.open',
    form: 'drawer',
    icon: 'refresh',
  },
  'node.mode.switch': {
    object: 'nodeMode',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'node.mode.switch',
    level: 'L2',
    form: 'confirm',
    icon: 'refresh',
  },
  'node.diagnostics.check': {
    object: 'diagnostics',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'node.diagnostics.check',
    form: 'none',
    icon: 'refresh',
  },
  'node.diagnostics.copyReport': {
    object: 'diagnostics',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'node.diagnostics.copyReport',
    form: 'none',
    icon: 'copy',
  },
  'node.diagnostics.openHint': {
    object: 'diagnostics',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'node.diagnostics.openHint',
    form: 'none',
    icon: 'arrow',
  },
  'backup.export': {
    object: 'backup',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'backup.export',
    form: 'none',
    audit: 'node.exported',
    icon: 'download',
  },
  'backup.import': {
    object: 'backup',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'backup.import',
    level: 'L2',
    form: 'confirm',
    audit: 'node.import',
    icon: 'download',
  },
  'forward.create': {
    object: 'forward',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'forward.create',
    level: 'L1',
    entry: '/hubs/:hubId/wireguard',
    form: 'drawer',
    audit: 'forward.created',
    icon: 'plus',
  },
  'forward.update': {
    object: 'forward',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'forward.update',
    level: 'L1',
    form: 'drawer',
    audit: 'forward.updated',
    icon: 'settings',
  },
  'forward.delete': {
    object: 'forward',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'forward.delete',
    level: 'L2',
    form: 'confirm',
    audit: 'forward.deleted',
    icon: 'x',
  },
  'forward.toggle': {
    object: 'forward',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'forward.toggle',
    level: 'L1',
    form: 'none',
    audit: 'forward.toggled',
    icon: 'pause',
  },
  'hostOps.processes': {
    object: 'hostOps',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.processes',
    form: 'drawer',
    audit: 'hostops.processes_viewed',
    icon: 'activity',
  },
  'hostOps.firewall': {
    object: 'hostOps',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.firewall',
    form: 'drawer',
    audit: 'hostops.firewall_viewed',
    icon: 'shield',
  },
  'hostOps.sshKeys': {
    object: 'hostOps',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.sshKeys',
    form: 'drawer',
    audit: 'hostops.keys_viewed',
    icon: 'key',
  },
  'hostOps.status': {
    object: 'hostOps',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.status',
    form: 'none',
    audit: 'hostops.status_fetched',
    icon: 'refresh',
  },
  // 能力占位（ui-redesign §4.4）：svc.restart 白名单动词待拍板，UI 恒禁用并提示原因；
  // Agent 侧能力落地后放开 rowActionDisabled 即可。
  'hostOps.svcRestart': {
    object: 'hostOps',
    kind: 'run',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.svcRestart',
    level: 'L2',
    form: 'confirm',
    audit: 'hostops.svc_restarted',
    icon: 'refresh',
  },
  'hostOps.fwApply': {
    object: 'firewall',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.fwApply',
    level: 'L2',
    form: 'inline',
    audit: 'hostops.fw_applied',
    icon: 'shield',
  },
  'hostOps.svcLogs': {
    object: 'service',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.svcLogs',
    form: 'drawer',
    audit: 'hostops.svc_logs_read',
    icon: 'terminal',
  },
  'hostOps.cronAdd': {
    object: 'cron',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.cronAdd',
    level: 'L1',
    form: 'drawer',
    audit: 'hostops.cron_added',
    icon: 'plus',
  },
  'hostOps.cronEdit': {
    object: 'cron',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.cronEdit',
    level: 'L1',
    form: 'drawer',
    audit: 'hostops.cron_edited',
    icon: 'settings',
  },
  'hostOps.cronToggle': {
    object: 'cron',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.cronToggle',
    form: 'none',
    audit: 'hostops.cron_toggled',
    icon: 'check',
  },
  'hostOps.cronRemove': {
    object: 'cron',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.cronRemove',
    level: 'L2',
    form: 'confirm',
    audit: 'hostops.cron_removed',
    icon: 'x',
  },
  'hostOps.fwConfirm': {
    object: 'firewall',
    kind: 'run',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.fwConfirm',
    form: 'none',
    audit: 'hostops.fw_confirmed',
    icon: 'check',
  },
  'hostOps.keyAdd': {
    object: 'sshKey',
    kind: 'create',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.keyAdd',
    level: 'L1',
    form: 'drawer',
    audit: 'hostops.key_added',
    icon: 'plus',
  },
  'hostOps.keyRemove': {
    object: 'sshKey',
    kind: 'delete',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.keyRemove',
    level: 'L2',
    form: 'confirm',
    audit: 'hostops.key_removed',
    icon: 'x',
  },
  'hostOps.onboard': {
    object: 'hostOps',
    kind: 'update',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.onboard',
    level: 'L1',
    form: 'wizard',
    audit: 'hostops.onboarded',
    icon: 'terminal',
  },
  'hostOps.probe': {
    object: 'hostOps',
    kind: 'read',
    scope: 'hub',
    parent: 'hub',
    label: 'hostOps.probe',
    form: 'none',
    audit: 'hostops.probed',
    icon: 'refresh',
  },
} as const satisfies Record<string, ActionMeta>;

export type ActionId = keyof typeof actions;
export type ActionRef = { readonly id: ActionId };

export function action(id: ActionId): ActionRef {
  return { id };
}

export function actionMeta(ref: ActionRef): ActionMeta {
  return actions[ref.id];
}

export function actionLabel(ref: ActionRef): string {
  return zh.actions[actions[ref.id].label];
}
